import { ARCHIVE_ENTITIES, entityKeyField } from '../archive';
import type { ArchiveEntity, ParsedArchive } from '../archive';
import { config } from '../config';
import { TABLE } from '../data/db';
import type { TableName } from '../data/db';
import { checkImportedRow } from '../data/rowSchemas';
import { MATCH_STATE } from '../domain/machine';
import { ARTIFACT_PHASE } from '../types';
import type {
  AnnotationRow,
  ArtifactPhase,
  CoachCardRow,
  FlagResolutionRow,
  HypothesisRow,
  MatchNoteRow,
  MatchRow,
  ObservationRow,
  OurPlayerRow,
  PlayerRow,
  SetPieceRow,
  SettingRow,
  TalkPointRow,
  TeamRow,
  TemplateRow,
} from '../types';

/**
 * **BAK-2's validation pass — untrusted bytes become rows here, or they do not become rows.**
 *
 * An import is the one place data enters this app without having been through the compiler,
 * `domain/`, or a repo. Everything downstream — INV-2's counts, INV-5's board cap, INV-6's talk
 * points, INV-11's render order — assumes rows that a handler produced. This module is where that
 * assumption is re-earned.
 *
 * It touches **no database**. Every decision it makes is a function of the archives it was handed,
 * which is what lets the whole of it run *before* the replace-only wipe destroys anything: an import
 * that discovered halfway through writing that a match was malformed would already have emptied the
 * profile it could no longer refill.
 *
 * ── Three severities, and each is a different requirement ────────────────────
 *
 * | Outcome | Applies to | Requirement |
 * |---|---|---|
 * | **quarantine the row** | a row the repo-boundary schema rejects (`data/rowSchemas.ts`) | BAK-2: *"quarantines violating rows with a surfaced error rather than accepting them (NFR-8)"* |
 * | **quarantine the match** | INV-6 violated, or more talk points of one phase than `TALKPOINT_COUNT` exists, or the `Match` row itself failed | §4 INV-6: *"The BAK-2/SYN-5 import path runs the same assertion and quarantines a violating match … an invariant enforced only by a UI is not an invariant"* |
 * | **report the reference** | a dangling `teamId`, `playerId`, `carriedFromMatchId`, `sourceHypothesisId` … | OBS-2's orphan report. Dropping a match because its `Team` row is missing destroys far more than the dangling pointer costs |
 *
 * A quarantined **match** takes its dependents with it, because a hypothesis whose match is absent
 * is unreachable from every screen and invisible to `cascadePlan`. A quarantined **row** takes only
 * what structurally cannot survive it: an `Observation` needs its hypothesis (or `reconcile` would
 * be asked for a count on a row that does not exist), and a `FlagResolution` needs its flag (DIST-1
 * reaches `matchId` through it).
 *
 * ── Order matters, and §11.9 says which order ────────────────────────────────
 *
 * SYN-5 restores from *many* archives. §11.9: *"match archives ascending by manifest `ts`, then the
 * profile archive last. Team and Player rows deduplicate by id with last-write-wins in that order …
 * This is **ordering, not merging** — ENV-10 forbids merge logic and none is introduced."* So this
 * module merges by **primary key, last one wins**, applied uniformly to every entity rather than
 * only to teams and players: one rule is checkable, and the caller owns the order.
 */

/** One archive to import, in the order the caller wants it applied (§11.9). */
export interface ArchiveInput {
  readonly bytes: Uint8Array;
  /** For diagnostics — a filename, or a manifest key. Never read as data. */
  readonly label?: string;
}

/** Audio metadata plus the bytes the container carried for it, if any. */
export interface ImportedBlob {
  readonly id: string;
  readonly mime: string;
  /** The **archived** byte count, preserved verbatim — see `putImportedBlob`. */
  readonly bytes: number;
  readonly complete: boolean;
  readonly createdAt: number;
  /** `null` when the metadata row had no `blobs/<id>` file behind it. The row is still restored. */
  readonly data: Uint8Array | null;
}

/** Everything that will be written, already validated and already ordered by primary key. */
export interface ImportedRows {
  readonly teams: readonly TeamRow[];
  readonly players: readonly PlayerRow[];
  readonly matches: readonly MatchRow[];
  readonly hypotheses: readonly HypothesisRow[];
  readonly observations: readonly ObservationRow[];
  readonly flagResolutions: readonly FlagResolutionRow[];
  readonly setPieces: readonly SetPieceRow[];
  readonly talkPoints: readonly TalkPointRow[];
  readonly matchNotes: readonly MatchNoteRow[];
  readonly annotations: readonly AnnotationRow[];
  readonly coachCards: readonly CoachCardRow[];
  readonly ourPlayers: readonly OurPlayerRow[];
  readonly audioBlobs: readonly ImportedBlob[];
  readonly templates: readonly TemplateRow[];
  readonly settings: readonly SettingRow[];
}

export interface QuarantinedRow {
  readonly entity: ArchiveEntity;
  /** The row's primary key, or `'(no key)'` when it did not carry one. */
  readonly key: string;
  readonly issues: readonly string[];
}

export interface QuarantinedMatch {
  readonly matchId: string;
  readonly reason: string;
  /** Rows dropped with it, by entity. Part of BAK-4's honesty: the user sees what was lost. */
  readonly droppedRows: Readonly<Partial<Record<ArchiveEntity, number>>>;
}

export interface ImportValidation {
  readonly rows: ImportedRows;
  /** How many rows of each entity will be written. The confirm's "what arrives" half. */
  readonly counts: Readonly<Record<ArchiveEntity, number>>;
  readonly quarantinedRows: readonly QuarantinedRow[];
  readonly quarantinedMatches: readonly QuarantinedMatch[];
  /** Rows dropped because the row they belong to is not being written. */
  readonly droppedForMissingOwner: readonly string[];
  /** References that point at nothing. Reported, never repaired, never a reason to drop a row. */
  readonly danglingReferences: readonly string[];
  /** `(matchId, phase)` sets whose archived ranks were not `1..n` and were rewritten. */
  readonly normalizedTalkPointRanks: readonly string[];
  /** `blobs/` files with no metadata row: nothing can be written for them, so the bytes are lost. */
  readonly blobBytesWithoutMetadata: readonly string[];
  /** `id: declared vs actual` where the archived byte count disagrees with the file. */
  readonly blobSizeMismatches: readonly string[];
  /** Metadata rows restored with no bytes behind them. The Observation and its minute survive (D-19). */
  readonly blobsRestoredEmpty: readonly string[];
}

const ENTITY_TABLE: Readonly<Record<ArchiveEntity, TableName>> = {
  teams: TABLE.teams,
  players: TABLE.players,
  matches: TABLE.matches,
  hypotheses: TABLE.hypotheses,
  observations: TABLE.observations,
  flagResolutions: TABLE.flagResolutions,
  setPieces: TABLE.setPieces,
  talkPoints: TABLE.talkPoints,
  matchNotes: TABLE.matchNotes,
  annotations: TABLE.annotations,
  coachCards: TABLE.coachCards,
  ourPlayers: TABLE.ourPlayers,
  audioBlobs: TABLE.audioBlobs,
  templates: TABLE.templates,
  settings: TABLE.settings,
};

function keyOf(row: unknown, field: string): string | null {
  if (typeof row !== 'object' || row === null) return null;
  const value = (row as Record<string, unknown>)[field];
  return typeof value === 'string' ? value : null;
}

/**
 * Merges every archive's rows for one entity, last-write-wins by primary key (§11.9).
 *
 * Returned sorted by key so the write order — and therefore every report path and every id-ordered
 * assertion downstream — is a function of the data rather than of the order the caller supplied.
 */
function mergeEntity(archives: readonly ParsedArchive[], entity: ArchiveEntity): unknown[] {
  const field = entityKeyField(entity);
  const byKey = new Map<string, unknown>();
  const keyless: unknown[] = [];
  for (const archive of archives) {
    for (const row of archive.entities[entity] ?? []) {
      const key = keyOf(row, field);
      if (key === null) keyless.push(row);
      else byKey.set(key, row);
    }
  }
  const merged = [...byKey.entries()].sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0));
  // A row with no primary key cannot be deduplicated and cannot be ordered; it is kept so the
  // schema can reject it by name rather than vanishing before anyone is told about it.
  return [...merged.map(([, row]) => row), ...keyless];
}

/** Validates one entity's merged rows, splitting them into accepted and quarantined. */
function validateEntity<T>(
  entity: ArchiveEntity,
  merged: readonly unknown[],
  quarantined: QuarantinedRow[],
): T[] {
  const field = entityKeyField(entity);
  const accepted: T[] = [];
  for (const row of merged) {
    const verdict = checkImportedRow(ENTITY_TABLE[entity], row);
    if (verdict.ok) accepted.push(verdict.row as T);
    else
      quarantined.push({
        entity,
        key: keyOf(row, field) ?? '(no key)',
        issues: verdict.issues,
      });
  }
  return accepted;
}

/**
 * INV-6, evaluated over the archive's own rows before anything is written.
 *
 * *"Exactly `TALKPOINT_COUNT` `TalkPoint` rows with `phase=fullTime` and ranks {1,2,3} exist for a
 * match in `archived` state that was not abandoned."* Checked here **and** re-asserted inside the
 * import transaction by `repos/talkPoints.assertInv6`, which is the same function
 * `CompleteDistillation` uses. Two runs of one rule, not two rules: this one decides *what to
 * quarantine* (a decision that must precede the wipe), and the in-transaction one is the
 * postcondition that catches a bug in this one by aborting rather than committing.
 */
function violatesInv6(match: MatchRow, fullTimePoints: readonly TalkPointRow[]): string | null {
  if (match.state !== MATCH_STATE.archived || match.abandoned) return null;
  const ranks = fullTimePoints.map((row) => row.rank).sort((a, b) => a - b);
  const expected = Array.from({ length: config.TALKPOINT_COUNT }, (_, i) => i + 1);
  if (ranks.length === expected.length && ranks.every((rank, i) => rank === expected[i])) {
    return null;
  }
  return `INV-6: an archived match needs exactly ${config.TALKPOINT_COUNT} full-time talk points ranked ${expected.join(', ')}; this one carried [${ranks.join(', ')}]`;
}

function groupKey(matchId: string, phase: ArtifactPhase): string {
  return `${matchId}/${phase}`;
}

/**
 * Turns parsed archives into the row set an import may write.
 *
 * @param archives already-parsed archives, **in application order** (§11.9). `readArchive` has
 *   already refused anything unreadable, so every fatal format decision is behind us.
 */
export function validateImport(archives: readonly ParsedArchive[]): ImportValidation {
  const quarantinedRows: QuarantinedRow[] = [];

  const teams = validateEntity<TeamRow>('teams', mergeEntity(archives, 'teams'), quarantinedRows);
  const players = validateEntity<PlayerRow>(
    'players',
    mergeEntity(archives, 'players'),
    quarantinedRows,
  );
  const matches = validateEntity<MatchRow>(
    'matches',
    mergeEntity(archives, 'matches'),
    quarantinedRows,
  );
  const hypotheses = validateEntity<HypothesisRow>(
    'hypotheses',
    mergeEntity(archives, 'hypotheses'),
    quarantinedRows,
  );
  const observations = validateEntity<ObservationRow>(
    'observations',
    mergeEntity(archives, 'observations'),
    quarantinedRows,
  );
  const flagResolutions = validateEntity<FlagResolutionRow>(
    'flagResolutions',
    mergeEntity(archives, 'flagResolutions'),
    quarantinedRows,
  );
  const setPieces = validateEntity<SetPieceRow>(
    'setPieces',
    mergeEntity(archives, 'setPieces'),
    quarantinedRows,
  );
  const talkPoints = validateEntity<TalkPointRow>(
    'talkPoints',
    mergeEntity(archives, 'talkPoints'),
    quarantinedRows,
  );
  const matchNotes = validateEntity<MatchNoteRow>(
    'matchNotes',
    mergeEntity(archives, 'matchNotes'),
    quarantinedRows,
  );
  const annotations = validateEntity<AnnotationRow>(
    'annotations',
    mergeEntity(archives, 'annotations'),
    quarantinedRows,
  );
  const coachCards = validateEntity<CoachCardRow>(
    'coachCards',
    mergeEntity(archives, 'coachCards'),
    quarantinedRows,
  );
  const ourPlayers = validateEntity<OurPlayerRow>(
    'ourPlayers',
    mergeEntity(archives, 'ourPlayers'),
    quarantinedRows,
  );
  const audioMeta = validateEntity<{
    id: string;
    mime: string;
    bytes: number;
    complete: boolean;
    createdAt: number;
  }>('audioBlobs', mergeEntity(archives, 'audioBlobs'), quarantinedRows);
  const templates = validateEntity<TemplateRow>(
    'templates',
    mergeEntity(archives, 'templates'),
    quarantinedRows,
  );
  const settings = validateEntity<SettingRow>(
    'settings',
    mergeEntity(archives, 'settings'),
    quarantinedRows,
  );

  // ── Match-level quarantine ─────────────────────────────────────────────────

  const talkPointsByGroup = new Map<string, TalkPointRow[]>();
  for (const row of talkPoints) {
    const key = groupKey(row.matchId, row.phase);
    const list = talkPointsByGroup.get(key);
    if (list) list.push(row);
    else talkPointsByGroup.set(key, [row]);
  }

  const quarantineReason = new Map<string, string>();
  for (const match of matches) {
    const fullTime = talkPointsByGroup.get(groupKey(match.id, ARTIFACT_PHASE.fullTime)) ?? [];
    const halftime = talkPointsByGroup.get(groupKey(match.id, ARTIFACT_PHASE.halftime)) ?? [];
    // `writeSelection` refuses more than `TALKPOINT_COUNT` of either phase, and it is the only
    // writer. Detecting it here turns an aborted import into one quarantined match.
    const over = [fullTime, halftime].find((rows) => rows.length > config.TALKPOINT_COUNT);
    if (over) {
      quarantineReason.set(
        match.id,
        `INV-6: a match may hold at most ${config.TALKPOINT_COUNT} ${over[0]?.phase ?? ''} talk points; this one carried ${over.length}`,
      );
      continue;
    }
    const inv6 = violatesInv6(match, fullTime);
    if (inv6) quarantineReason.set(match.id, inv6);
  }

  // A `Match` row the schema rejected is already out; every row that names it must follow, or the
  // import writes hypotheses and observations no screen can reach and no cascade can delete.
  for (const entry of quarantinedRows) {
    if (entry.entity === 'matches' && entry.key !== '(no key)') {
      quarantineReason.set(entry.key, 'the Match row itself failed validation');
    }
  }

  const liveMatchIds = new Set(
    matches.filter((match) => !quarantineReason.has(match.id)).map((match) => match.id),
  );

  const droppedForMissingOwner: string[] = [];
  const perMatchDrops = new Map<string, Partial<Record<ArchiveEntity, number>>>();

  function countDrop(entity: ArchiveEntity, matchId: string): void {
    const bucket = perMatchDrops.get(matchId) ?? {};
    bucket[entity] = (bucket[entity] ?? 0) + 1;
    perMatchDrops.set(matchId, bucket);
  }

  function ofLiveMatch<T extends { id: string; matchId: string }>(
    entity: ArchiveEntity,
    rows: readonly T[],
  ): T[] {
    return rows.filter((row) => {
      if (liveMatchIds.has(row.matchId)) return true;
      if (quarantineReason.has(row.matchId)) countDrop(entity, row.matchId);
      else droppedForMissingOwner.push(`${entity}[${row.id}]: no match ${row.matchId}`);
      return false;
    });
  }

  const keptHypotheses = ofLiveMatch('hypotheses', hypotheses);
  const keptSetPieces = ofLiveMatch('setPieces', setPieces);
  const keptTalkPoints = ofLiveMatch('talkPoints', talkPoints);
  const keptNotes = ofLiveMatch('matchNotes', matchNotes);
  // B-1: the two match-scoped B entities follow their match exactly like the four above. A
  // dangling `hypothesisId` inside a kept row is reported below, never a reason to drop it —
  // nothing downstream aborts on it (unlike an increment, whose missing hypothesis would
  // break `reconcile`).
  const keptAnnotations = ofLiveMatch('annotations', annotations);
  const keptCoachCards = ofLiveMatch('coachCards', coachCards);

  const hypothesisIds = new Set(keptHypotheses.map((row) => row.id));
  const keptObservations = ofLiveMatch('observations', observations).filter((row) => {
    // An increment whose hypothesis is gone would make `reconcile` produce a count for a row that
    // does not exist, and `writeCountState` would abort the whole import on it. A flag legitimately
    // has no hypothesis (FLAG-2) and is unaffected.
    if (row.hypothesisId === null || hypothesisIds.has(row.hypothesisId)) return true;
    droppedForMissingOwner.push(`observations[${row.id}]: no hypothesis ${row.hypothesisId}`);
    return false;
  });

  const observationIds = new Set(keptObservations.map((row) => row.id));
  const keptResolutions = flagResolutions.filter((row) => {
    // DIST-1 deliberately gives `FlagResolution` no `matchId` — it is reached through the flag
    // Observation (§3.1). With the flag gone the row names nothing and cannot be cascaded.
    if (observationIds.has(row.observationId)) return true;
    droppedForMissingOwner.push(`flagResolutions[${row.id}]: no flag ${row.observationId}`);
    return false;
  });

  // ── Talk-point ranks: `writeSelection` rewrites `1..n`, so say so when it will ──

  const normalizedTalkPointRanks: string[] = [];
  const orderedTalkPoints: TalkPointRow[] = [];
  const keptByGroup = new Map<string, TalkPointRow[]>();
  for (const row of keptTalkPoints) {
    const key = groupKey(row.matchId, row.phase);
    const list = keptByGroup.get(key);
    if (list) list.push(row);
    else keptByGroup.set(key, [row]);
  }
  for (const [key, group] of [...keptByGroup.entries()].sort(([a], [b]) => (a < b ? -1 : 1))) {
    const sorted = [...group].sort((a, b) => a.rank - b.rank || (a.id < b.id ? -1 : 1));
    if (sorted.some((row, index) => row.rank !== index + 1)) {
      normalizedTalkPointRanks.push(
        `${key}: [${sorted.map((row) => row.rank).join(', ')}] → [${sorted.map((_, i) => i + 1).join(', ')}]`,
      );
    }
    orderedTalkPoints.push(...sorted);
  }

  // ── Audio: metadata and bytes, paired ──────────────────────────────────────

  const bytesById = new Map<string, Uint8Array>();
  for (const archive of archives) {
    for (const [id, data] of archive.blobs) bytesById.set(id, data);
  }

  const blobSizeMismatches: string[] = [];
  const blobsRestoredEmpty: string[] = [];
  const audioBlobs: ImportedBlob[] = audioMeta.map((meta) => {
    const data = bytesById.get(meta.id) ?? null;
    if (data === null) blobsRestoredEmpty.push(meta.id);
    else if (data.length !== meta.bytes)
      blobSizeMismatches.push(`${meta.id}: declared ${meta.bytes}, file ${data.length}`);
    return { ...meta, data };
  });

  const metadataIds = new Set(audioMeta.map((meta) => meta.id));
  const blobBytesWithoutMetadata = [...bytesById.keys()].filter((id) => !metadataIds.has(id)).sort();

  // ── Dangling references — reported, never repaired ─────────────────────────

  const teamIds = new Set(teams.map((row) => row.id));
  const playerIds = new Set(players.map((row) => row.id));
  const setPieceIds = new Set(keptSetPieces.map((row) => row.id));
  const keptHypothesisIds = new Set(keptHypotheses.map((row) => row.id));
  const danglingReferences: string[] = [];

  const note = (path: string, target: string): void => {
    danglingReferences.push(`${path} → ${target}`);
  };
  for (const row of matches) if (!teamIds.has(row.teamId)) note(`matches[${row.id}].teamId`, row.teamId);
  for (const row of players)
    if (!teamIds.has(row.teamId)) note(`players[${row.id}].teamId`, row.teamId);
  for (const row of keptHypotheses) {
    if (row.playerId !== null && !playerIds.has(row.playerId))
      note(`hypotheses[${row.id}].playerId`, row.playerId);
    if (row.carriedFromMatchId !== null && !liveMatchIds.has(row.carriedFromMatchId))
      note(`hypotheses[${row.id}].carriedFromMatchId`, row.carriedFromMatchId);
  }
  for (const row of orderedTalkPoints) {
    if (row.sourceHypothesisId !== null && !keptHypothesisIds.has(row.sourceHypothesisId))
      note(`talkPoints[${row.id}].sourceHypothesisId`, row.sourceHypothesisId);
    if (row.attachedSetPieceId !== null && !setPieceIds.has(row.attachedSetPieceId))
      note(`talkPoints[${row.id}].attachedSetPieceId`, row.attachedSetPieceId);
  }
  for (const row of keptResolutions) {
    if (row.promotedHypothesisId !== null && !keptHypothesisIds.has(row.promotedHypothesisId))
      note(`flagResolutions[${row.id}].promotedHypothesisId`, row.promotedHypothesisId);
  }
  for (const row of keptObservations) {
    if (row.audioBlobId !== null && !metadataIds.has(row.audioBlobId))
      note(`observations[${row.id}].audioBlobId`, row.audioBlobId);
  }
  for (const row of keptNotes) {
    if (row.audioBlobId !== null && !metadataIds.has(row.audioBlobId))
      note(`matchNotes[${row.id}].audioBlobId`, row.audioBlobId);
  }
  for (const row of keptAnnotations) {
    if (!keptHypothesisIds.has(row.hypothesisId))
      note(`annotations[${row.id}].hypothesisId`, row.hypothesisId);
  }
  for (const row of keptCoachCards) {
    for (const sourceId of row.selectedHypothesisIds) {
      if (!keptHypothesisIds.has(sourceId))
        note(`coachCards[${row.id}].selectedHypothesisIds`, sourceId);
    }
    for (const entry of row.adjustments) {
      for (const sourceId of entry.sourceHypothesisIds) {
        if (!keptHypothesisIds.has(sourceId))
          note(`coachCards[${row.id}].adjustments[${entry.entryId}].sourceHypothesisIds`, sourceId);
      }
    }
  }

  const rows: ImportedRows = {
    teams,
    players,
    matches: matches.filter((match) => liveMatchIds.has(match.id)),
    hypotheses: keptHypotheses,
    observations: keptObservations,
    flagResolutions: keptResolutions,
    setPieces: keptSetPieces,
    talkPoints: orderedTalkPoints,
    matchNotes: keptNotes,
    annotations: keptAnnotations,
    coachCards: keptCoachCards,
    ourPlayers,
    audioBlobs,
    templates,
    settings,
  };

  const counts = Object.fromEntries(
    ARCHIVE_ENTITIES.map((entity) => [entity, rows[entity].length]),
  ) as Record<ArchiveEntity, number>;

  const quarantinedMatches: QuarantinedMatch[] = [...quarantineReason.entries()]
    .sort(([a], [b]) => (a < b ? -1 : 1))
    .map(([matchId, reason]) => ({
      matchId,
      reason,
      droppedRows: perMatchDrops.get(matchId) ?? {},
    }));

  return {
    rows,
    counts,
    quarantinedRows,
    quarantinedMatches,
    droppedForMissingOwner: droppedForMissingOwner.sort(),
    danglingReferences: danglingReferences.sort(),
    normalizedTalkPointRanks,
    blobBytesWithoutMetadata,
    blobSizeMismatches,
    blobsRestoredEmpty: blobsRestoredEmpty.sort(),
  };
}
